import React, {useMemo} from 'react';
import {useDndContext} from '@dnd-kit/core';
import * as css from './TimetableGrid.css';
import LectureTimeRow from '../lectureTimeRow/LectureTimeRow';
import BreakCardRow from '../breakCardRow/BreakCardRow';
import EmptyDay from '../emptyDay/EmptyDay';
import {computeDropIndicator, getSlotKey} from '../../hooks/timetableSort';
import {TTimetableItem, TLectureTimeGroup, TTimeGroup} from '../../../../redux/timetable/timetable.type';

interface TimetableGridHandlers {
    onAddLecture?: (tsid: number | null) => void;
    onAddBreak?: () => void;
    onMenuClick?: (item: TTimetableItem) => void;
    onTicketsClick?: (item: TTimetableItem) => void;
    onEditLecture?: (item: TTimetableItem) => void;
}

interface TimetableGridProps {
    timeGroups: TTimeGroup[];
    expanded?: boolean;
    handlers?: TimetableGridHandlers;
}

function TimetableGrid({
    timeGroups = [] as TTimeGroup[],
    expanded = true,
    handlers = {},
}: TimetableGridProps) {
    const {
        onAddLecture = () => {},
        onAddBreak,
        onMenuClick = () => {},
        onTicketsClick = () => {},
        onEditLecture = () => {},
    } = handlers;

    const {active, over} = useDndContext();
    const activeId = active ? Number(active.id) : null;
    const overId = over ? Number(over.id) : null;
    const indicator = useMemo(
        () => computeDropIndicator(activeId, overId, timeGroups.filter(g => !('isBreak' in g)) as TLectureTimeGroup[]),
        [activeId, overId, timeGroups]
    );

    if(timeGroups.length === 0) {
        return (
            <css.container>
                <EmptyDay onAddLecture={() => onAddLecture(null)} onAddBreak={onAddBreak} />
            </css.container>
        );
    }

    const firstLectureIdx = timeGroups.findIndex(g => !('isBreak' in g));

    return (
        <css.container>
            {timeGroups.map((group, i) => (
                <React.Fragment key={'isBreak' in group ? `break-${group.breakItem.ttid}` : `row-${group.time}`}>
                    {'isBreak' in group ? (
                        <BreakCardRow
                            time={group.time}
                            breakItem={group.breakItem}
                            onEdit={() => onMenuClick(group.breakItem)}
                        />
                    ) : (
                        <LectureTimeRow
                            timeGroup={group}
                            expanded={expanded}
                            isFirst={i === firstLectureIdx}
                            dropIdx={indicator && indicator.slotKey === getSlotKey(group as TLectureTimeGroup) ? indicator.idx : undefined}
                            onAddLecture={(tsid) => onAddLecture(tsid)}
                            onTicketsClick={onTicketsClick as () => void}
                            onEditLecture={onEditLecture}
                        />
                    )}
                    {i < timeGroups.length - 1 && <css.separator />}
                </React.Fragment>
            ))}
        </css.container>
    );
}

export default TimetableGrid;
